import { Box, CircularProgress, Grid, Paper, Typography } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BackButton } from '../components/BackButton'
import { ConversationImageList } from '../components/ConversationImageList'
import { ConversationList } from '../components/ConversationList'
import { CustomExplain } from '../components/CustomExplain'
import { CustomTitle } from '../components/CustomTitile'
import { EditButton } from '../components/EditButton'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { SaveButton } from '../components/SaveButton'
import { loadStatus } from '../constants/loadStatus'
import { useAuth } from '../hooks/useAuth'
import { useC802ConversationGet } from '../hooks/useC802ConversationGet'
import { useC804ConversationRecordSave } from '../hooks/useC804ConversationRecordSave'
import { useConversation } from '../hooks/useConversation'
import type {ConversationIllustrationInfo,ConversationInfo, ConversationVisualDisplayItem, RealtimeDiagramData, VisualsItemMap, } from '../interfaces/ConversationIf'
import { LanguageContext } from '../util/LanguageContext'
import { SideMenu } from './SideMenu'
import { RealtimeDiagramPreview } from '../components/RealtimeDiagramPreview'

const buildVisualDisplayItems = (
  visualsWithRealtimeIds: string[] | undefined,
  visualsItemMap: VisualsItemMap | undefined,
  illustrations: ConversationIllustrationInfo[],
  realtimeDiagram: RealtimeDiagramData | null
): ConversationVisualDisplayItem[] => {
  if (!visualsWithRealtimeIds || !visualsItemMap) {
    return []
  }

  const result: ConversationVisualDisplayItem[] = []

  for (const visualId of visualsWithRealtimeIds) {
    const visual = visualsItemMap[visualId]
    if (!visual) continue

    if (visual.type === 'registered_illustration') {
      const matched = illustrations.find(img => img.illustration_id === visual.illustration_id)
      if (!matched) continue

      result.push({
        visualId,
        type: 'registered_illustration',
        illustration: matched,
      })
    } else if (visual.type === 'realtime_diagram') {
      if (!realtimeDiagram) continue

      result.push({
        visualId,
        type: 'realtime_diagram',
        diagram: realtimeDiagram,
      })
    }
  }

  return result
}

// 会話内容画面
export const Conversation = () => {
  const auth = useAuth()
  const conv = useConversation()
  const c802 = useC802ConversationGet()
  const c804 = useC804ConversationRecordSave()

  // 文字列変換用
  const translation = useContext(LanguageContext)

  // ローディングステータス監視
  useEffect(() => {
    if (c802.loading === loadStatus.LOADING || c804.loading === loadStatus.LOADING) {
      auth.setIsDisable(true)
    } else {
      auth.setIsDisable(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c802.loading, c804.loading])
  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')

  // 会話配列
  const [convs, setConvs] = useState<ConversationInfo[]>([])
  // image配列
  const [images, setImages] = useState<ConversationIllustrationInfo[]>([])
  const [tmpImages, setTmpImages] = useState<ConversationIllustrationInfo[]>([])
  // 編集モード
  const [editMode, setEditMode] = useState(false)

  // 保存ボタン
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)

  // 画像削除状態
  const [isImageDeleted, setIsImageDeleted] = useState(false)

  // リアルタイム図解
  const [realtimeDiagram, setRealtimeDiagram] = useState<RealtimeDiagramData | null>(null)

  // VISUALS関連（新版データ用）
  const [visualsVersion, setVisualsVersion] = useState<number | undefined>(undefined)
  const [visualsWithRealtimeIds, setVisualsWithRealtimeIds] = useState<string[] | undefined>(undefined)
  const [visualsItemMap, setVisualsItemMap] = useState<VisualsItemMap | undefined>(undefined)
  const [editVisualsWithRealtimeIds, setEditVisualsWithRealtimeIds] = useState<string[] | undefined>(undefined)
  const [editVisualsItemMap, setEditVisualsItemMap] = useState<VisualsItemMap | undefined>(undefined)
  const [tmpEditVisualsWithRealtimeIds, setTmpEditVisualsWithRealtimeIds] = useState<string[] | undefined>(undefined)
  const [tmpEditVisualsItemMap, setTmpEditVisualsItemMap] = useState<VisualsItemMap | undefined>(undefined)
  const [tmpRealtimeDiagram, setTmpRealtimeDiagram] = useState<RealtimeDiagramData | null>(null)
  const [deletedRealtimeVisualIds, setDeletedRealtimeVisualIds] = useState<string[]>([])

  useEffect(() => {
    const fetchData = async () => {
      const result = await c802.execConversationGet(conv.convId)
      if (result.success) {
        if (result.info) {
          const info = result.info

          info.conversationList.forEach(conversation => {
            // 会話記録を編集対象に設定
            if (conv.orgText) {
              conversation.text_before_editing = conversation.org_text
              conversation.text_being_edited = conversation.org_text
              conversation.text_editing_temporary_save = conversation.org_text
            } else {
              conversation.text_before_editing = conversation.edit_text
              conversation.text_being_edited = conversation.edit_text
              conversation.text_editing_temporary_save = conversation.edit_text
            }
          })

          // 画像
          if (conv.orgText) {
            setImages(info.org_illustrationList)
            setTmpImages(info.org_illustrationList)
          } else {
            setImages(info.edit_illustrationList)
            setTmpImages(info.edit_illustrationList)
          }

          // 会話
          setConvs(info.conversationList)

          // リアルタイム図解
          setRealtimeDiagram(info.realtimeDiagram ?? null)
          setTmpRealtimeDiagram(info.realtimeDiagram ?? null)

          // VISUALS関連（旧版データでは undefined / [] のまま）
          setVisualsVersion(info.visualsVersion)
          setVisualsWithRealtimeIds(info.visualsWithRealtimeIds ?? undefined)
          setVisualsItemMap(info.visualsItemMap ?? undefined)

          setEditVisualsWithRealtimeIds(info.editVisualsWithRealtimeIds ?? undefined)
          setEditVisualsItemMap(info.editVisualsItemMap ?? undefined)

          setTmpEditVisualsWithRealtimeIds(info.editVisualsWithRealtimeIds ?? undefined)
          setTmpEditVisualsItemMap(info.editVisualsItemMap ?? undefined)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const navigate = useNavigate()

  // 会話編集ハンドラ
  const convEditHandler = () => {
    setEditMode(true)
  }
  // 会話編集キャンセルハンドラ
  const convCancelHandler = () => {
    // 会話を元に戻す。
    convs.forEach(conversation => {
      if (conv.orgText) {
        conversation.text_before_editing = conversation.org_text
        conversation.text_being_edited = conversation.org_text
        conversation.text_editing_temporary_save = conversation.org_text
      } else {
        conversation.text_before_editing = conversation.edit_text
        conversation.text_being_edited = conversation.edit_text
        conversation.text_editing_temporary_save = conversation.edit_text
      }
    })
    setImages(tmpImages)
    setEditVisualsWithRealtimeIds(tmpEditVisualsWithRealtimeIds)
    setEditVisualsItemMap(tmpEditVisualsItemMap)
    setRealtimeDiagram(tmpRealtimeDiagram)
    setDeletedRealtimeVisualIds([])
    setEditMode(false)
    setIsButtonDisabled(true)
  }
  // 会話編集保存ハンドラ
  const convSaveHandler = async () => {
    setIsButtonDisabled(true)
    setEditMode(false)
    const result = await c804.execConversationRecordSave(conv.convId, convs, images, deletedRealtimeVisualIds)
    if (result.success) {
      navigate('/conversationLog')
    } else {
      // 会話を元に戻す。
      convs.forEach(conversation => {
        if (conv.orgText) {
          conversation.text_before_editing = conversation.org_text
          conversation.text_being_edited = conversation.org_text
          conversation.text_editing_temporary_save = conversation.org_text
        } else {
          conversation.text_before_editing = conversation.edit_text
          conversation.text_being_edited = conversation.edit_text
          conversation.text_editing_temporary_save = conversation.edit_text
        }
      })
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }

  /**
   * 新版VISUALSデータかどうか。
   *
   * 旧版では BackEnd が visuals* を返さないため undefined。
   * 新版では空配列でも visualsVersion 等が存在する。
   */
  const isVisualsRecord =
    visualsVersion !== undefined ||
    visualsWithRealtimeIds !== undefined ||
    visualsItemMap !== undefined ||
    editVisualsWithRealtimeIds !== undefined ||
    editVisualsItemMap !== undefined

  // 会話イメージ削除ハンドラ
  const conDelImageHandler = (id: string) => {
    if (editMode) {
      setImages(prevImages => prevImages.filter(img => img.illustration_id !== id))

      if (isVisualsRecord) {
        setEditVisualsWithRealtimeIds(prev => {
          const baseIds = prev !== undefined ? prev : visualsWithRealtimeIds
          const baseMap = editVisualsItemMap !== undefined ? editVisualsItemMap : visualsItemMap

          if (!baseIds) return []
          if (!baseMap) return baseIds

          return baseIds.filter(visualId => {
            const item = baseMap[visualId]
            if (!item) return false

            if (
              item.type === 'registered_illustration' &&
              item.illustration_id === id
            ) {
              return false
            }

            return true
          })
        })

        setEditVisualsItemMap(prev => {
          const baseMap = prev !== undefined ? prev : visualsItemMap
          if (!baseMap) return {}

          const next: VisualsItemMap = {}

          Object.entries(baseMap).forEach(([visualId, item]) => {
            if (
              item.type === 'registered_illustration' &&
              item.illustration_id === id
            ) {
              return
            }

            next[visualId] = item
          })

          return next
        })
      }

      setIsButtonDisabled(false)
      setIsImageDeleted(true)
    }
  }

  const conDelRealtimeDiagramHandler = (visualId: string) => {
    if (editMode) {
      setDeletedRealtimeVisualIds(prev => {
        if (prev.includes(visualId)) return prev
        return [...prev, visualId]
      })

      setEditVisualsWithRealtimeIds(prev => {
        const baseIds = prev !== undefined ? prev : visualsWithRealtimeIds
        return (baseIds ?? []).filter(id => id !== visualId)
      })

      setEditVisualsItemMap(prev => {
        const baseMap = prev !== undefined ? prev : visualsItemMap
        if (!baseMap) return {}

        const next = { ...baseMap }
        delete next[visualId]
        return next
      })

      setRealtimeDiagram(null)
      setIsButtonDisabled(false)
    }
  }

  // ダイアログクローズ
  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
  }
  //多言語化対応
  const userLanguage = navigator.language || navigator.languages[0]
  const baseLanguage = userLanguage.split('-')[0]
  let conversationContentJsx: JSX.Element
  switch (baseLanguage) {
    case 'es':
      conversationContentJsx = (
        <Typography
          sx={{
            font: 'normal normal bold 32px/48px Noto Sans JP',
            fontSize: '28px',
          }}
        >
          {translation['label_conversation_content']}
        </Typography>
      )
      break
    default:
      conversationContentJsx = (
        <Typography
          sx={{
            font: 'normal normal bold 32px/48px Noto Sans JP',
          }}
        >
          {translation['label_conversation_content']}
        </Typography>
      )
      break
  }

  /**
   * 編集後VISUALSが存在するか。
   *
   * [] は「編集後に全削除された状態」として有効。
   */
  const hasEditVisuals =
    editVisualsWithRealtimeIds !== undefined ||
    editVisualsItemMap !== undefined

  const useVisualTimelineDisplay = isVisualsRecord

  /**
   * 表示対象VISUALS。
   *
   * 編集モード中に編集後VISUALSがある場合は編集後を優先。
   * 編集後VISUALSが [] でも元に戻さない。
   */
  const displayVisualsWithRealtimeIds =
    editMode && hasEditVisuals
      ? editVisualsWithRealtimeIds ?? []
      : conv.orgText
        ? visualsWithRealtimeIds ?? []
        : hasEditVisuals
          ? editVisualsWithRealtimeIds ?? []
          : visualsWithRealtimeIds ?? []

  const displayVisualsItemMap =
    editMode && hasEditVisuals
      ? editVisualsItemMap ?? {}
      : conv.orgText
        ? visualsItemMap ?? {}
        : hasEditVisuals
          ? editVisualsItemMap ?? {}
          : visualsItemMap ?? {}

  const visualDisplayItems = useVisualTimelineDisplay
    ? buildVisualDisplayItems(
        displayVisualsWithRealtimeIds,
        displayVisualsItemMap,
        images,
        realtimeDiagram
      )
    : []

  /**
   * 左側VISUALS/図解エリアを表示するか。
   *
   * 旧版:
   *   登録図解 images があれば表示。
   *
   * 新版:
   *   visualDisplayItems が1件以上なら表示。
   *   visuals* が空なら表示しない。
   */
  const hasVisualArea = useVisualTimelineDisplay
    ? visualDisplayItems.length > 0
    : images.length > 0

  return (
    <>
      {openDialog && (
        <ModalConfirmDialog
          open={openDialog}
          text={errorDialog}
          onClick={() => handleDialogClose()}
        />
      )}
      <Box sx={{ display: 'flex', p: 0 }}>
        <Box sx={{ p: 0, width: '338px', flexShrink: 0 }}>
          <SideMenu />
        </Box>
        <Box sx={{ flexGrow: 1 }} display="flex" flexDirection="column">
          <Box marginTop={13}>
            <Box>
              <CustomTitle>{translation['label_conversation_history']}</CustomTitle>
            </Box>
            <Box>
              <CustomExplain>{translation['message_conversation_history_info']}</CustomExplain>
            </Box>
            <Box
              sx={{
                marginTop: '40px',
              }}
            >
              <Paper
                sx={{
                  margin: '0px 64px 0px 0px',
                  maxWidth: '100%',
                  width: '1418px',
                  overflowX: 'auto',
                  height: 'calc(100vh - 330px)',
                }}
                elevation={2}
              >
                <Box
                  sx={{
                    p: '10px',
                    height: 50,
                    bgcolor: editMode ? '#fff16f' : 'white',
                  }}
                >
                  <Grid container alignItems="center" justifyContent="space-around" spacing={2}>
                    <Grid item xs={3}>
                      {!editMode && (
                        <BackButton
                          title={translation['button_back_to_list']}
                          onClick={() => navigate('/conversationLog')}
                        />
                      )}
                    </Grid>
                    <Grid item xs={4} textAlign={'center'}>
                      {conversationContentJsx}
                    </Grid>
                    <Grid item xs={3}>
                      <Box display="flex" justifyContent="flex-end" sx={{ gap: '16px' }}>
                        {editMode ? (
                          <>
                            <BackButton
                              title={translation['button_cancel']}
                              onClick={convCancelHandler}
                            />
                            <SaveButton
                              title={translation['button_save']}
                              onClick={convSaveHandler}
                              isButtonDisabled={isButtonDisabled}
                            />
                          </>
                        ) : (
                          <EditButton
                            title={translation['button_edit']}
                            onClick={convEditHandler}
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Grid
                  container
                  spacing={2}
                  justifyContent="space-around"
                  style={{
                    height: 'calc(100vh - 400px)',
                    overflow: 'auto',
                    marginTop: '0px',
                  }}
                >
                  {!hasVisualArea ? (
                    <Grid
                      item
                      xs={12}
                      style={{
                        paddingTop: '0px',
                      }}
                    >
                      <ConversationList
                        editMode={editMode}
                        items={convs}
                        setItems={setConvs}
                        setIsButtonDisabled={setIsButtonDisabled}
                        isImageDeleted={isImageDeleted}
                      />
                    </Grid>
                  ) : (
                    <>
                      <Grid
                        item
                        xs={3}
                        style={{
                          paddingTop: '0px',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: '16px',
                            pt: '16px',
                            px: '12px',
                            width: '100%',
                            boxSizing: 'border-box',
                          }}
                        >
                          {useVisualTimelineDisplay ? (
                            visualDisplayItems.length > 0 ? (
                              visualDisplayItems.map(item => {
                                if (item.type === 'realtime_diagram') {
                                  return (
                                    <Box
                                      key={item.visualId}
                                      sx={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <RealtimeDiagramPreview
                                        diagram={item.diagram}
                                        editMode={editMode}
                                        visualId={item.visualId}
                                        onDeleteRealtimeDiagram={conDelRealtimeDiagramHandler}
                                      />
                                    </Box>
                                  )
                                }

                                return (
                                  <Box
                                    key={item.visualId}
                                    sx={{
                                      width: '100%',
                                      display: 'flex',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <ConversationImageList
                                      editMode={editMode}
                                      items={[item.illustration]}
                                      onDeleteImage={conDelImageHandler}
                                    />
                                  </Box>
                                )
                              })
                            ) : null
                          ) : (
                            <>
                              {/* 旧版互換: 従来表示 */}
                              {images.length > 0 && (
                                <Box
                                  sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <ConversationImageList
                                    editMode={editMode}
                                    items={images}
                                    onDeleteImage={conDelImageHandler}
                                  />
                                </Box>
                              )}
                            </>
                          )}
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={9}
                        style={{
                          paddingTop: '0px',
                          paddingLeft: '0px',
                        }}
                      >
                        <ConversationList
                          editMode={editMode}
                          items={convs}
                          setItems={setConvs}
                          setIsButtonDisabled={setIsButtonDisabled}
                          isImageDeleted={isImageDeleted}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            </Box>
            <Box
              sx={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 9999,
              }}
            >
              {(c802.loading === loadStatus.LOADING || c804.loading === loadStatus.LOADING) && (
                <CircularProgress />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}
